import { Method2fa } from '@/types/enums/auth/Method2fa';
import { makeFetcher } from '@/utils/fetch';
import { config } from '@/config';
import { AuthStatus } from '@/types/enums/auth/AuthStatus';
import { LOGIN_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { EnrollRequestData } from '@/types/types/forms/auth/auth.interface';

interface LoginCompletionResponse {
    status: AuthStatus;
    messagePhraseId?: number;
    destinationUrl?: string;
}

const makeAuthService = (apiUrl: string) => {
    const fetcher = makeFetcher(apiUrl);

    return {
        login: async (email: string, password: string, rememberMe: boolean) => {
            const requestBody = JSON.stringify({
                email,
                password,
                rememberMe,
            });
            const res = await fetcher.post(`/login`, requestBody);
            const jsonRes = (await res.json()) as LoginCompletionResponse;
            return { status: jsonRes.status };
        },

        enroll: async (requestData: EnrollRequestData) => {
            const res = await fetcher.post(`/enroll`, JSON.stringify(requestData));
            const jsonRes = (await res.json()) as LoginCompletionResponse;
            return { status: jsonRes.status };
        },

        checkCaptcha: async (captchaCode: string) => {
            const res = await fetcher.post(
                '/captcha',
                JSON.stringify({ captchaResponse: captchaCode }),
                { customErrorPhraseId: { 401: LOGIN_ERROR_PHRASES.LOGIN_COOKIE_PROBLEM } },
            );
            const jsonRes = (await res.json()) as LoginCompletionResponse;
            return { status: jsonRes.status };
        },

        get2faMethods: async () => {
            const res = await fetcher.get(`/2fa/methods`);
            const methods = (await res.json()) as Method2fa[];
            return methods;
        },

        send2faMethod: async (method: Method2fa | undefined) => {
            const res = await fetcher.post(`/2fa/send`, JSON.stringify({ method }));
            const { codeId, phoneNumber } = (await res.json()) as {
                codeId: string;
                phoneNumber?: string;
            };
            return { codeId, phoneNumber };
        },

        validate2faCode: async (
            method: Method2fa,
            code: string | undefined,
            codeId: string | undefined,
        ) => {
            const res = await fetcher.post(
                `/2fa/validate`,
                JSON.stringify({ method, code, codeId: codeId }),
            );
            const jsonRes = (await res.json()) as LoginCompletionResponse;
            return { status: jsonRes.status };
        },

        requestPasswordReset: async (email: string, captchaResponse: string) => {
            const res = await fetcher.post(
                `/password-reset/request`,
                JSON.stringify({
                    email,
                    captchaResponse,
                }),
            );
            const { message } = (await res.json()) as { message: string };
            return message;
        },

        passwordReset: async (newPassword: string, confirmNewPassword: string, token: string) => {
            const res = await fetcher.post(
                '/password-reset',
                JSON.stringify({
                    newPassword,
                    confirmNewPassword,
                    token,
                }),
            );
            const { message } = (await res.json()) as {
                message: string;
            };
            return message;
        },

        passwordHardReset: async (
            newPassword: string,
            confirmNewPassword: string,
            token: string,
        ) => {
            const res = await fetcher.post(
                '/hard-reset-password',
                JSON.stringify({
                    newPassword,
                    confirmNewPassword,
                    token,
                }),
            );
            const { status } = (await res.json()) as {
                status: AuthStatus;
            };
            return status;
        },

        logout: async () => {
            const res = await fetcher.post('/logout', undefined);
            return res.status;
        },

        loginOnRegComplete: async (registrationId: string) => {
            const res = await fetcher.post(
                `/login/registration`,
                JSON.stringify({
                    registrationId,
                }),
            );
            const { status } = (await res.json()) as { status: AuthStatus };
            return status;
        },
    };
};

export const authService = makeAuthService(config.api.url.auth);
